import React from "react";
import { Box, Paper, Typography } from "@mui/material";

const normalizeLeaveKey = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const toTitle = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());

const resolveLeaveLabel = (balanceKey, leaveTypes) => {
  const normalizedKey = normalizeLeaveKey(balanceKey);
  const found = (leaveTypes || []).find((entry) => {
    const candidates = [entry?.key, entry?.code, entry?.label, entry?.name];
    return candidates.some((candidate) => normalizeLeaveKey(candidate) === normalizedKey);
  });
  return found?.label || found?.name || toTitle(balanceKey);
};

export default function LeaveBalancesSummary({
  balances = {},
  leaveTypes = [],
  title = "Remaining Leave Credits",
  emptyText = "No leave balance data available.",
}) {
  const allowedKeys = new Set((leaveTypes || [])
    .flatMap((entry) => [entry?.key, entry?.code, entry?.label, entry?.name])
    .map(normalizeLeaveKey)
    .filter(Boolean));
  const entries = Object.entries(balances || {}).filter(([key]) => {
    if (allowedKeys.size === 0) return true;
    return allowedKeys.has(normalizeLeaveKey(key));
  });

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {title}
      </Typography>
      {entries.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {emptyText}
        </Typography>
      ) : (
        <Box>
          {entries.map(([key, value]) => {
            const remaining = Number(value?.remaining || 0);
            const max = Number(value?.max || 0);
            const used = Number(value?.used || 0);
            const label = resolveLeaveLabel(key, leaveTypes);
            const summary = Number.isFinite(max) && max > 0
              ? `${remaining} remaining of ${max} day(s)`
              : `${remaining} day(s) remaining`;
            return (
              <Typography key={key} variant="body2" sx={{ mb: 0.5 }}>
                {label}: {summary}
                {used > 0 ? `, ${used} used` : ""}
              </Typography>
            );
          })}
        </Box>
      )}
    </Paper>
  );
}
